/**
 * <ReadBadge> — compact inline badge for `lx_read` calls. Just shows
 * "Read <path>" with the optional line range; avoids dumping full file
 * contents into the chat surface. Click-to-expand showing the read
 * range is a followup.
 */
import { FileText } from "lucide-react";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

interface ReadBadgeProps {
  block: ToolUseBlockType;
}

function readInput(input: unknown): {
  path: string;
  offset?: number;
  limit?: number;
} | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Record<string, unknown>;
  const path = typeof r.path === "string" ? r.path : "";
  if (!path) return null;
  return {
    path,
    offset: typeof r.offset === "number" ? r.offset : undefined,
    limit: typeof r.limit === "number" ? r.limit : undefined,
  };
}

export function ReadBadge({ block }: ReadBadgeProps) {
  const parsed = readInput(block.input);
  if (!parsed) {
    return (
      <div
        data-testid="read-badge"
        data-parse-failed="true"
        className="my-1 inline-flex items-center gap-1.5 rounded border border-cream-300 bg-cream-100/30 px-2 py-1 text-caption text-coffee-500 dark:border-coffee-700 dark:bg-coffee-900/30"
      >
        <FileText size={12} />
        Read (unparseable input)
      </div>
    );
  }
  const range =
    parsed.offset != null && parsed.limit != null
      ? ` (lines ${parsed.offset}–${parsed.offset + parsed.limit - 1})`
      : parsed.offset != null
        ? ` (from line ${parsed.offset})`
        : "";
  return (
    <div
      data-testid="read-badge"
      className="my-1 inline-flex items-center gap-1.5 rounded border border-cream-300 bg-cream-100/30 px-2 py-1 text-caption text-coffee-700 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300"
    >
      <FileText size={12} />
      <span className="font-mono">{parsed.path}</span>
      <span className="text-coffee-500 dark:text-cream-500">{range}</span>
    </div>
  );
}
